import type { GuestDetail } from '@camp67/model';
import { parseDate } from '@camp67/model/booking/dates';
import { reservationStore } from '../../../state/reservation-store';
import { useReservationStore } from '../../../state/use-reservation-store';
import type { NivaasPackage } from '../../../types/package';
import type { TentType, UseBookingFormV4Return } from '../types';

/**
 * Hook that wraps reservationStore for V4 booking form.
 * Provides a clean interface for all booking form state and actions.
 */
export function useBookingFormV4(formId: string): UseBookingFormV4Return {
  // Subscribe to store updates
  useReservationStore(formId);

  const reservation = reservationStore.state.reservation;
  const primaryContact = reservationStore.state.primaryContact;

  // Parse dates from strings
  const startDate = reservation.startDate ? parseDate(reservation.startDate) : null;
  const endDate = reservation.endDate ? parseDate(reservation.endDate) : null;

  // Build room counts map - rebuild on every render since the store
  // mutates the array in place and useMemo won't see changes
  // The component re-renders when the store changes via useReservationStore
  const roomCounts = new Map<TentType, number>();
  for (const [type, count] of reservation.rooms) {
    roomCounts.set(type as TentType, count);
  }

  return {
    // State
    selectedPackage: reservationStore.getPackage(),
    rooms: reservation.rooms as [TentType, number][],
    roomCounts,
    startDate,
    endDate,
    adults: reservation.guests.adult,
    children: reservation.guests.children,
    guests: reservation.guests.details,
    primaryContactId: primaryContact.id,
    primaryEmail: primaryContact.email,
    primaryPhone: reservationStore.getPhoneNumber(),
    countryCode: reservationStore.getCountryCode(),
    availableDates: reservationStore.getAvailableDates(),
    availableTentTypes: reservationStore.getTentTypes() as TentType[],
    pricing: reservationStore.cost,
    maxCapacity: reservationStore.getCapacityOfRooms(),

    // Actions
    selectPackage: (pkg: NivaasPackage | null) => {
      reservationStore.setPackage(pkg);
    },

    addRoom: (type: TentType) => {
      reservationStore.addRoom(type);
    },

    removeRoom: (type: TentType) => {
      reservationStore.removeRoom(type);
    },

    setStartDate: (date: Date | undefined) => {
      reservationStore.chooseDateStart(date);
    },

    setEndDate: (date: Date | undefined) => {
      reservationStore.chooseDateRange(date);
    },

    clearDates: () => {
      reservationStore.chooseDateStart(undefined);
      reservationStore.chooseDateRange(undefined);
    },

    setAdults: (count: number) => {
      reservationStore.setAdultCount(count);
    },

    setChildren: (count: number) => {
      reservationStore.setChildCount(count);
    },

    updateGuest: (id: string, detail: Partial<GuestDetail>) => {
      reservationStore.updateGuestDetail(id, detail);
    },

    setPrimaryContact: (id: string) => {
      reservationStore.choosePrimaryContact(id);
    },

    setEmail: (email: string) => {
      reservationStore.setPrimaryEmail(email);
    },

    setPhone: (phone: string) => {
      reservationStore.setPrimaryPhoneNumber(phone);
    },

    setCountryCode: (code: string) => {
      reservationStore.setCountryCode(code);
    },

    // Validation
    isPackageStepComplete: true, // Package selection is always complete (can be null)
    isRoomStepComplete: reservationStore.isStep1Complete(),
    isDateGuestStepComplete: reservationStore.isStep2Complete(),
    isGuestDetailsStepComplete: reservationStore.isStep3Complete(),
    canSubmit: reservationStore.canSubmit,
    validate: () => reservationStore.validate(),

    // Errors
    emailError: reservationStore.getError('email'),
    phoneError: reservationStore.getError('phone'),
  };
}
